import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVendorBalance,
  getVendorPayouts,
  requestPayout,
} from '../../redux/slices/vendorPayoutSlice';
import { DollarSign, TrendingUp, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const VendorPayouts = () => {
  const dispatch = useDispatch();
  const { balance, payouts, loading, requestLoading, error } = useSelector(
    (state) => state.vendorPayouts
  );

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestData, setRequestData] = useState({
    amount: '',
    paymentMethod: 'bank_transfer',
    vendorNotes: '',
  });

  useEffect(() => {
    dispatch(getVendorBalance());
    dispatch(getVendorPayouts({}));
  }, [dispatch]);

  const handleRequestPayout = async () => {
    if (!requestData.amount || requestData.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (requestData.amount > balance?.availableBalance) {
      toast.error('Amount exceeds available balance');
      return;
    }

    try {
      await dispatch(
        requestPayout({
          amount: Number(requestData.amount),
          paymentMethod: requestData.paymentMethod,
          vendorNotes: requestData.vendorNotes,
        })
      ).unwrap();

      toast.success('Payout request submitted successfully');
      setShowRequestModal(false);
      setRequestData({ amount: '', paymentMethod: 'bank_transfer', vendorNotes: '' });
      
      // Refresh balance
      dispatch(getVendorBalance());
    } catch (err) {
      toast.error(err.message || 'Failed to request payout');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      approved: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      processing: { color: 'bg-purple-100 text-purple-700', icon: Clock },
      completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Payouts</h1>
        <p className="text-gray-600 mt-1">Manage your earnings and payout requests</p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow">
          <h3 className="text-white/80 text-sm mb-2">Total Earnings</h3>
          <p className="text-3xl font-bold">₹{balance?.totalEarnings?.toLocaleString() || 0}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow">
          <h3 className="text-white/80 text-sm mb-2">Available Balance</h3>
          <p className="text-3xl font-bold">
            ₹{balance?.availableBalance?.toLocaleString() || 0}
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow">
          <h3 className="text-white/80 text-sm mb-2">Pending Payouts</h3>
          <p className="text-3xl font-bold">
            ₹{balance?.pendingPayouts?.toLocaleString() || 0}
          </p>
        </div>

        <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white p-6 rounded-lg shadow">
          <h3 className="text-white/80 text-sm mb-2">Total Paid Out</h3>
          <p className="text-3xl font-bold">
            ₹{balance?.totalPaidOut?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      {/* Request Payout Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowRequestModal(true)}
          disabled={!balance || balance.availableBalance <= 0}
          className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <DollarSign className="w-5 h-5" />
          Request Payout
        </button>
        {balance && balance.availableBalance <= 0 && (
          <p className="text-sm text-gray-600 mt-2">
            No available balance for payout
          </p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Payout History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">Payout History</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
          </div>
        ) : payouts.length === 0 ? (
          <div className="p-12 text-center">
            <DollarSign className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold mb-2">No Payout Requests</h3>
            <p className="text-gray-600">You haven't requested any payouts yet</p>
          </div>
        ) : (
          <div className="divide-y">
            {payouts.map((payout) => (
              <div key={payout._id} className="p-6 hover:bg-gray-50">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-lg">
                        ₹{payout.requestedAmount.toLocaleString()}
                      </h3>
                      {getStatusBadge(payout.status)}
                    </div>
                    <p className="text-sm text-gray-600">
                      Requested on{' '}
                      {new Date(payout.createdAt).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-gray-600">Payment Method</p>
                    <p className="font-medium">{payout.paymentMethod.replace('_', ' ')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Orders Included</p>
                    <p className="font-medium">{payout.orders?.length || 0}</p>
                  </div>
                  {payout.transactionId && (
                    <div>
                      <p className="text-gray-600">Transaction ID</p>
                      <p className="font-medium font-mono text-xs">
                        {payout.transactionId}
                      </p>
                    </div>
                  )}
                  {payout.transactionDate && (
                    <div>
                      <p className="text-gray-600">Processed Date</p>
                      <p className="font-medium">
                        {new Date(payout.transactionDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

                {payout.rejectionReason && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-sm text-red-700">
                      <strong>Rejection Reason:</strong> {payout.rejectionReason}
                    </p>
                  </div>
                )}

                {payout.vendorNotes && (
                  <div className="mt-4 p-3 bg-gray-50 rounded">
                    <p className="text-sm text-gray-700">
                      <strong>Your Notes:</strong> {payout.vendorNotes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request Payout Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Request Payout</h3>

            <div className="mb-6 p-4 bg-blue-50 rounded">
              <p className="text-sm text-gray-700">
                <strong>Available Balance:</strong> ₹
                {balance?.availableBalance?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Minimum payout: ₹1,000
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Amount to Withdraw *
                </label>
                <input
                  type="number"
                  value={requestData.amount}
                  onChange={(e) =>
                    setRequestData({ ...requestData, amount: e.target.value })
                  }
                  placeholder="Enter amount"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  min="1000"
                  max={balance?.availableBalance}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Payment Method *
                </label>
                <select
                  value={requestData.paymentMethod}
                  onChange={(e) =>
                    setRequestData({ ...requestData, paymentMethod: e.target.value })
                  }
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={requestData.vendorNotes}
                  onChange={(e) =>
                    setRequestData({ ...requestData, vendorNotes: e.target.value })
                  }
                  placeholder="Add any notes for the admin"
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setRequestData({
                    amount: '',
                    paymentMethod: 'bank_transfer',
                    vendorNotes: '',
                  });
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestPayout}
                disabled={requestLoading}
                className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {requestLoading ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorPayouts;
